import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, inr } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import ComboSelect from "@/components/ComboSelect";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { HandCoins, ArrowCircleDown, ArrowCircleUp } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";

export default function CashVouchers() {
    const { user } = useAuth();
    const [ledgers, setLedgers] = useState([]);
    const [vouchers, setVouchers] = useState([]);
    const [locations, setLocations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [cashLedger, setCashLedger] = useState(null); // CASH IN HAND ledger — the only ledger this page ever touches
    const [busy, setBusy] = useState(false);

    const today = () => new Date().toISOString().slice(0, 10);
    const emptyForm = () => ({
        date: today(),
        kind: "receive",
        counterparty_ledger_id: "",
        amount: "",
        description: "",
        dept: "",
        location: "",
    });
    const [form, setForm] = useState(emptyForm());

    const load = async () => {
        const [led, locs, deps, v] = await Promise.all([
            api.get("/ledgers"),
            api.get("/masters/locations"),
            api.get("/masters/departments"),
            api.get("/cash-vouchers"),
        ]);
        // Standard Cash Vouchers post to CASH IN HAND (party_type='cash').
        // FIAT BANK is intentionally excluded — that's the Fiat channel's home.
        const cash =
            led.data.find((l) => l.name === "CASH IN HAND") ||
            led.data.find((l) => l.party_type === "cash") ||
            null;
        setCashLedger(cash);
        setLedgers(led.data);
        setLocations(locs.data);
        setDepartments(deps.data);
        // Only show vouchers that belong to CASH IN HAND — this page is CASH-only by design
        setVouchers(cash ? v.data.filter((x) => x.cash_ledger_id === cash.id) : v.data);
    };
    useEffect(() => { load(); }, []);

    const canPost = user?.role === "founder" || user?.role === "accountant";

    const submit = async () => {
        if (!cashLedger?.id) return toast.error("CASH IN HAND ledger not found — seed data may be missing");
        if (!form.counterparty_ledger_id) return toast.error("Choose a counterparty ledger");
        const amt = Number(form.amount);
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        if (!form.description?.trim()) return toast.error("Add a short description");
        setBusy(true);
        try {
            await api.post("/cash-vouchers", {
                date: form.date,
                kind: form.kind,
                cash_ledger_id: cashLedger.id,
                counterparty_ledger_id: form.counterparty_ledger_id,
                amount: amt,
                description: form.description.trim(),
                dept: form.dept || null,
                location: form.location || null,
            });
            toast.success(form.kind === "receive" ? "Cash receipt posted" : "Cash payment posted");
            setForm(emptyForm());
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const reverse = async (v) => {
        const reason = window.prompt("Reason for reversal?");
        if (!reason) return;
        try {
            await api.post(`/cash-vouchers/${v.id}/reverse`, null, { params: { reason }});
            toast.success("Reversed");
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    // Counterparty options: filter out banks/cash themselves (avoid Dr FIAT / Cr FIAT nonsense)
    const cpartyOptions = ledgers
        .filter((l) => !["bank", "cash"].includes(l.party_type))
        .map((l) => ({ id: l.id, name: l.name, hint: l.group_name }));

    return (
        <>
            <Toaster richColors position="top-right" />
            <PageHeader title="Cash vouchers" sub="Real hard-cash inflows and outflows. Money you receive or spend outside the bank statement — always posted against CASH IN HAND." />

            <div className="p-8 space-y-6">
                <div className="bg-white border rounded-md" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--se-border)" }}>
                        <div>
                            <div className="font-serif text-lg flex items-center gap-2">
                                <HandCoins size={18} /> New voucher
                            </div>
                            <div className="text-xs text-stone-500">
                                Direct-post to the ledger — no draft, no upload. Posts against{" "}
                                <span className="font-medium text-stone-700">{cashLedger?.name || "CASH IN HAND"}</span>.
                            </div>
                        </div>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className="flex gap-2" data-testid="cv-kind-toggle">
                            <button
                                onClick={() => setForm((f) => ({ ...f, kind: "receive" }))}
                                className={`flex-1 py-3 px-4 rounded-sm border text-left transition ${form.kind === "receive" ? "border-emerald-600 bg-emerald-50" : "border-stone-200 bg-white"}`}
                                data-testid="cv-kind-receive"
                            >
                                <div className="flex items-center gap-2 text-sm font-medium" style={{ color: form.kind === "receive" ? "#065f46" : "#57534e" }}>
                                    <ArrowCircleDown size={16} /> Cash received
                                </div>
                                <div className="text-[11px] text-stone-500 mt-0.5">Money in from a customer / other party</div>
                            </button>
                            <button
                                onClick={() => setForm((f) => ({ ...f, kind: "spend" }))}
                                className={`flex-1 py-3 px-4 rounded-sm border text-left transition ${form.kind === "spend" ? "border-rose-600 bg-rose-50" : "border-stone-200 bg-white"}`}
                                data-testid="cv-kind-spend"
                            >
                                <div className="flex items-center gap-2 text-sm font-medium" style={{ color: form.kind === "spend" ? "#9f1239" : "#57534e" }}>
                                    <ArrowCircleUp size={16} /> Cash spent
                                </div>
                                <div className="text-[11px] text-stone-500 mt-0.5">Money out to a vendor / expense</div>
                            </button>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <Label>Date</Label>
                                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="cv-date" />
                            </div>
                            <div>
                                <Label>Amount (₹)</Label>
                                <Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" data-testid="cv-amount" />
                            </div>
                            <div>
                                <Label>Dept <span className="text-stone-400">(optional)</span></Label>
                                <ComboSelect
                                    value={form.dept}
                                    onValueChange={(v) => setForm({ ...form, dept: v })}
                                    options={departments.map((d) => ({ id: d.name, name: d.name }))}
                                    placeholder="—"
                                    testId="cv-dept"
                                    allowClear
                                />
                            </div>
                            <div className="col-span-3">
                                <Label>{form.kind === "receive" ? "Received from" : "Paid to"}</Label>
                                <ComboSelect
                                    value={form.counterparty_ledger_id}
                                    onValueChange={(v) => setForm({ ...form, counterparty_ledger_id: v })}
                                    options={cpartyOptions}
                                    placeholder={form.kind === "receive" ? "Customer / income head" : "Vendor / expense head"}
                                    testId="cv-counterparty"
                                />
                            </div>
                            <div className="col-span-3">
                                <Label>Description</Label>
                                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was this for? (e.g. Petrol - Van, Cash sale to walk-in customer)" data-testid="cv-desc" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setForm(emptyForm())} data-testid="cv-reset">Reset</Button>
                            <Button onClick={submit} disabled={busy || !canPost} style={{ background: form.kind === "receive" ? "#059669" : "#e11d48", color: "#fff" }} data-testid="cv-submit">
                                {busy ? "Posting…" : form.kind === "receive" ? "Post cash receipt" : "Post cash payment"}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <div className="px-5 py-3 border-b flex items-center justify-between gap-4" style={{ borderColor: "var(--se-border)" }}>
                        <div>
                            <div className="font-serif text-lg">Cash vouchers · {cashLedger?.name || "CASH IN HAND"}</div>
                            <div className="text-[11px] text-stone-500 mt-0.5">
                                Shows only direct-post cash vouchers. For petty cash disbursements, loan payments, and bank-statement lines, {cashLedger
                                    ? <Link to={`/ledgers/${cashLedger.id}`} className="underline hover:text-blue-700" data-testid="cv-view-all-link">view all {cashLedger.name} activity →</Link>
                                    : "open the CASH IN HAND ledger detail"}.
                            </div>
                        </div>
                    </div>
                    <table className="se-table" data-testid="cv-table">
                        <thead><tr>
                            <th>Date</th><th>Kind</th><th>Counterparty</th>
                            <th>Description</th><th className="text-right">Amount</th>
                            <th>Status</th><th>Action</th>
                        </tr></thead>
                        <tbody>
                            {vouchers.length === 0 ? (
                                <tr><td colSpan="7" className="py-10 text-center text-stone-500">No vouchers yet. Post one above.</td></tr>
                            ) : vouchers.map((v) => (
                                <tr key={v.id} data-testid={`cv-row-${v.id}`}>
                                    <td className="font-mono text-xs">{v.date}</td>
                                    <td>
                                        <span className={`se-chip ${v.kind === "receive" ? "se-chip-posted" : "se-chip-warn"}`}>
                                            {v.kind === "receive" ? "received" : "spent"}
                                        </span>
                                    </td>
                                    <td className="text-xs">{v.counterparty_ledger_name}</td>
                                    <td className="text-xs max-w-[300px] truncate" title={v.description}>{v.description}</td>
                                    <td className="se-num" style={{ color: v.kind === "receive" ? "#059669" : "#9f1239" }}>
                                        {v.kind === "receive" ? "+ " : "- "}₹ {inr(v.amount)}
                                    </td>
                                    <td><StatusChip status={v.status} /></td>
                                    <td>
                                        {v.status === "posted" && canPost ? (
                                            <Button size="sm" variant="outline" onClick={() => reverse(v)} data-testid={`cv-reverse-${v.id}`}>Reverse</Button>
                                        ) : null}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
